import * as XLSX from 'xlsx';

import { FileResultType } from '../../Helper/file';
import { ODAType, ChargeType } from './Create/type';

export function downloadOdaCharges(pincodes: Array<ChargeType>, fileName: string) {
  /* generate worksheet and workbook */
  const rows = pincodes.map(p => [p.pincode, String(p.charge), p.applyOn.join(','), (p.canDeliver && 'Yes') || 'No']);
  const worksheet = XLSX.utils.aoa_to_sheet([]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Dates');

  XLSX.utils.sheet_add_aoa(worksheet, [
    ['Pincode', 'Charge Amount', 'Global Charges', 'Deliver Serviceability'],
    ...rows,
  ]);

  /* calculate column width */
  const max_width = rows.reduce((w, r) => Math.max(w, r[0]?.length ?? 0), 10);

  worksheet['!cols'] = [{ wch: max_width }, { wch: 12 }];
  /* create an XLSX file and try to save to Presidents.xlsx */
  XLSX.writeFile(workbook, `${fileName} - ODA.xlsx`, { compression: true });
}

export function readXslAndGetObject(data: FileResultType[]) {
  const [{ fileData }] = data;
  if (!(fileData instanceof Array)) return;

  const {
    data: [headers, ...body],
  } = fileData[0] as { data: string[][] };

  const countryIndex = headers.findIndex(x => x.match(/country/i));
  const stateIndex = headers.findIndex(x => x.match(/state/i));
  const cityIndex = headers.findIndex(x => x.match(/city/i));
  const pincodeIndex = headers.findIndex(x => x.match(/pincode/i));
  const chargeIndex = headers.findIndex(x => x.match(/charge amount/i));
  const globalChargesIndex = headers.findIndex(x => x.match(/global charges/i));
  const deliveryServiceIndex = headers.findIndex(x => x.toLowerCase().match(/delivery serviceability/i));
  const chargeTypeIndex = headers.findIndex(x => x.match(/charge type/i));
  const minimumChargeIndex = headers.findIndex(x => x.match(/minimum charge/i));
  const maximumChargeIndex = headers.findIndex(x => x.match(/maximum charge/i));

  if (countryIndex === -1) throw { msg: 'Country column not found in xlsx' };
  if (!body.length) throw { msg: 'At least one country entry required' };
  if (deliveryServiceIndex === -1) throw { msg: 'Delivery serviceability column not found in xlsx' };

  const emptyCountryRows = body.filter(row => !row[countryIndex]);
  if (emptyCountryRows.length) {
    throw { msg: 'Country value is mandatory and cannot be empty' };
  }

  const invalidDeliveryServiceValue = body.filter(row => {
    return !row[deliveryServiceIndex]?.toLowerCase().match(/^(Y|N|yes|no)$/i);
  });

  if (invalidDeliveryServiceValue.length) {
    const countries = invalidDeliveryServiceValue.map(i => i[countryIndex]);

    const formattedError =
      countries.length === 1
        ? `${countries[0]} contains invalid entry of delivery serviceability.`
        : `Given countries contain invalid entry of delivery serviceability: ${countries.join(', ')}`;

    throw { msg: formattedError };
  }

  const deliveryCharges: ODAType['charges'] = body.map(arr => ({
    country: arr[countryIndex],
    state: stateIndex !== -1 ? arr[stateIndex] : undefined,
    city: cityIndex !== -1 ? arr[cityIndex] : undefined,
    pincode: pincodeIndex !== -1 ? arr[pincodeIndex] : undefined,
    charge: arr[chargeIndex] ? Number(arr[chargeIndex]) : 0,

    chargeType: chargeTypeIndex !== -1 ? arr[chargeTypeIndex] || undefined : undefined,

    minCharge: minimumChargeIndex !== -1 && arr[minimumChargeIndex] ? Number(arr[minimumChargeIndex]) : undefined,

    maxCharge: maximumChargeIndex !== -1 && arr[maximumChargeIndex] ? Number(arr[maximumChargeIndex]) : undefined,

    applyOn: arr[globalChargesIndex]?.split(',') || [],
    canDeliver: /yes|Y/i.test(arr[deliveryServiceIndex]),
  }));

  return deliveryCharges;
}

export default { downloadOdaCharges, readXslAndGetObject };
